import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { UserApi } from '../../api/UserApi';
import { AICheckerState } from '../../interface/AICheckerState';
import { ApplicationState } from '../../interface/ApplicationState';
import { BotExchange } from '../../interface/BotExchange';
import { updateLocalUser } from '../../redux/actions/userActions';

export const supportsAI = (exchange?: BotExchange) =>
  exchange === BotExchange.binance || exchange === BotExchange.binanceUS || exchange === BotExchange.binanceTestnet;

const statuses: Record<string, string> = {
  running: 'Reviewing markets', decided: 'Decision ready', hold: 'Holding', executed: 'Trade completed',
  error: 'Check failed', submitting: 'Submitting order', order_unknown: 'Order needs review',
};
const actions: Record<string, string> = { BUY: 'Buy', TAKE_PROFIT: 'Take profit', STOP_LOSS: 'Stop loss', HOLD: 'Hold' };
const dateLabel = (date?: string) => date && !Number.isNaN(Date.parse(date)) ? new Date(date).toLocaleString() : '—';

export const AICheckerSettings = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: ApplicationState) => state.userStore.user);
  const profileLoading = useSelector((state: ApplicationState) => state.userStore.isGettingUserProfile);
  const profileSaving = useSelector((state: ApplicationState) => state.userStore.isSettingUserProfile);
  const [state, setState] = useState<AICheckerState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const requestId = useRef(0);

  useEffect(() => {
    if (!user._id) return;
    let active = true;
    let pending = false;
    const id = ++requestId.current;
    const load = async () => {
      if (pending) return;
      pending = true;
      setLoading(true);
      try {
        const result = await new UserApi().getAIState();
        if (result === undefined) throw new Error('No response');
        if (active && id === requestId.current) { setState(result); setError(''); }
      } catch {
        if (active && id === requestId.current) setError('Unable to load AIChecker status. Please refresh to try again.');
      } finally {
        pending = false;
        if (active && id === requestId.current) setLoading(false);
      }
    };
    void load();
    const timer = window.setInterval(() => { void load(); }, 60000);
    return () => { active = false; window.clearInterval(timer); };
  }, [user._id, refresh, profileSaving]);

  useEffect(() => { setState(null); setError(''); }, [user._id]);
  const supported = supportsAI(user.botExchange);
  const uncertain = state?.status === 'order_unknown' || state?.status === 'submitting';

  return <Stack spacing={2}>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
      <FormControlLabel label="Enable AIChecker" control={<Switch checked={!!user.isAIEnabled}
        disabled={profileLoading || profileSaving || (!supported && !user.isAIEnabled)}
        onChange={event => dispatch(updateLocalUser({ isAIEnabled: event.target.checked }))} />} />
      <Chip label="At most once per hour" variant="outlined" />
    </Stack>
    <Typography variant="body2" color="text.secondary">
      AIChecker reviews your holdings, technical indicators, signals and news to buy, take profit, stop loss or hold.
      It discovers Binance spot markets using your funding currency and can trade coins outside your saved pairs.
    </Typography>
    <Alert severity="info">
      AI controls entry and exit timing, including when to take a loss. Your configured indicators, pair list,
      stop-loss and take-profit rules are bypassed. Maximum buy and sell amounts still apply.
      Decisions can place one market order per hour. Save your settings to apply changes.
    </Alert>
    {!supported && <Alert severity="warning">AIChecker supports Binance, Binance.US and Binance TestNet. Select one of these exchanges or disable AIChecker before saving an active trading configuration.</Alert>}
    {user.isAIEnabled && !user.tradingEnabled && <Alert severity="info">Trading is paused. Enable Trading in Trading Settings and save to allow AIChecker to trade.</Alert>}
    <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>Latest saved activity</Typography>
        <Button size="small" startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          disabled={loading || !user._id} onClick={() => setRefresh(value => value + 1)}>Refresh status</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {loading && !state && <Typography role="status" variant="body2">Loading AIChecker activity…</Typography>}
      {!loading && !state && !error && <Typography variant="body2" color="text.secondary">No AIChecker activity yet. After enabling and saving, the bot will check when it next runs.</Typography>}
      {state && <Stack spacing={1.5} sx={{ opacity: error ? 0.65 : 1 }}>
        {error && <Typography variant="caption">Showing previously loaded activity.</Typography>}
        <Chip sx={{ alignSelf: 'flex-start' }} label={statuses[state.status] || 'Unknown status'}
          color={uncertain || state.status === 'error' ? 'warning' : state.status === 'executed' ? 'success' : 'default'} />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 0.5, md: 3 }}>
          <Typography variant="body2">Last check: {dateLabel(state.lastAttemptAt)}</Typography>
          <Typography variant="body2">Next eligible check: {dateLabel(state.nextRunAt)}</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">Checks require saved AI settings and trading to be enabled. Status refreshes every minute.</Typography>
        {state.decision && <Box>
          <Typography fontWeight={600}>
            {actions[state.decision.action] || state.decision.action}
            {state.decision.symbol ? ` · ${state.decision.symbol}` : ''}
          </Typography>
          {state.decision.action !== 'HOLD' && <Typography variant="body2">
            Requested amount: {state.decision.quoteAmount} in the selected market’s quote currency
          </Typography>}
          <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>{state.decision.reason}</Typography>
        </Box>}
        {state.error && <Alert severity="warning">{state.error}</Alert>}
        {uncertain && <Alert severity="warning">Further AI trades are blocked while this order is being submitted or needs reconciliation. If this status persists, contact support to review the exchange order. Switching AI off and on does not reset it.</Alert>}
      </Stack>}
    </Box>
  </Stack>;
};
